import classNames from 'classnames'
import { type FC, type ReactNode, useState } from 'react'
import { Col, Container, Row, Tab } from 'react-bootstrap'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Button } from '../../components/Button/Button'
import { LinkButton } from '../../components/Button/LinkButton'
import { Spinner } from '../../components/Spinner/Spinner'
import { Tabs } from '../../components/Tabs/Tabs'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import { type SchedulingFilters } from '../../models/Scheduling'
import { selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { useGetUserSchedulingSettingsQuery } from '../../store/scheduling/scheduling'
import { useAppSelector } from '../../store/hooks'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { resolveActiveTabKey, type SchedulingTabKey } from './bookingTabs'
import { SchedulingFilterBar } from './FilterBar/SchedulingFilterBar'
import { isMeetingPlatformBroken } from './flows/form/scheduleHelpers'
import { SchedulingOnboardingFlow } from './onboarding/SchedulingOnboardingFlow'
import { MeetingPlatformWarning } from './shared/MeetingPlatformWarning'
import { SchedulingStatusCard } from './SchedulingStatusCard'
import { CancelledTab } from './tabs/CancelledTab'
import { HistoryTab } from './tabs/HistoryTab'
import { RequestsTab } from './tabs/RequestsTab'
import { ScheduledTab } from './tabs/ScheduledTab'
import { SuggestedTab } from './tabs/SuggestedTab'
import { WaitingTab } from './tabs/WaitingTab'
import { useSchedulingPageStatus } from './useSchedulingPageStatus'
import styles from './SchedulingPage.module.scss'

interface SchedulingTabDefinition {
  key: SchedulingTabKey
  title: string
  subtitle: string
  hasAlertBadge: boolean
  icon?: string
  content: (filters: SchedulingFilters) => ReactNode
}

const TAB_DEFINITIONS: SchedulingTabDefinition[] = [
  {
    key: 'requests',
    title: 'Requests',
    subtitle: 'Client requests waiting for your response.',
    hasAlertBadge: true,
    content: (filters) => <RequestsTab filters={filters} />,
  },
  {
    key: 'suggested',
    title: 'Suggested',
    subtitle: 'Meetings your AI Assistant suggests you book.',
    hasAlertBadge: false,
    content: (filters) => <SuggestedTab filters={filters} />,
  },
  {
    key: 'waiting',
    title: 'Waiting',
    subtitle: 'Booking links are ready. Waiting for clients to pick a time.',
    hasAlertBadge: false,
    content: (filters) => <WaitingTab filters={filters} />,
  },
  {
    key: 'scheduled',
    title: 'Scheduled',
    subtitle: 'Meetings with a time on the calendar.',
    hasAlertBadge: false,
    content: (filters) => <ScheduledTab filters={filters} />,
  },
  {
    key: 'cancelled',
    title: 'Canceled',
    subtitle: 'Meetings canceled by clients.',
    hasAlertBadge: false,
    content: (filters) => <CancelledTab filters={filters} />,
  },
  {
    key: 'history',
    title: 'History',
    subtitle: "Bookings that are no longer active, including ones you've archived.",
    hasAlertBadge: false,
    icon: 'bi-clock-history',
    content: (filters) => <HistoryTab filters={filters} />,
  },
]

const TabTitle: FC<{ definition: SchedulingTabDefinition; count: number }> = ({ definition, count }) => (
  <div className={classNames('d-flex align-items-center zocks-gap-0.5', { 'zocks-text-gray-700': definition.icon })}>
    {definition.icon ? <i className={`bi ${definition.icon}`} aria-hidden='true' /> : null}
    <span>{definition.title}</span>
    {count > 0 ? (
      <span className={classNames('badge rounded-pill', definition.hasAlertBadge ? 'bg-danger text-white' : 'zocks-bg-color-gray-200 zocks-text-gray-600')}>
        {count}
      </span>
    ) : null}
  </div>
)

export const SchedulingPage: FC = () => {
  const { activeTab } = useParams()
  const navigate = useNavigate()
  const { trackingClient } = useTracking()
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const [filters, setFilters] = useState<SchedulingFilters>(() => ({ userIds: [currentUser.id] }))
  const [meetingPlatformSkipped, setMeetingPlatformSkipped] = useState(false)
  const { status, refetch, counts, eventTypes, showRequestsTab, hasCompletedOnboarding } = useSchedulingPageStatus(filters, meetingPlatformSkipped)
  const resolvedTab = resolveActiveTabKey(activeTab, showRequestsTab)
  const { data: userSettings } = useGetUserSchedulingSettingsQuery({ accountId: currentUser.accountId, userId: currentUser.id })
  const showPlatformWarning = userSettings ? isMeetingPlatformBroken(userSettings) : false
  const activeTabTitle = TAB_DEFINITIONS.find((definition) => definition.key === resolvedTab)?.title ?? 'Requests'
  useDocumentTitle(`Scheduling - ${activeTabTitle} | Zocks`)

  if (resolvedTab !== activeTab) {
    return <Navigate to={`/scheduling/${resolvedTab}`} replace />
  }

  if (status.kind === 'loading') {
    return (
      <Container>
        <Row className='mb-4'>
          <Col>
            <h3 className='mb-0'>Scheduling</h3>
          </Col>
        </Row>
        <div className='d-flex justify-content-center py-5'>
          <Spinner />
        </div>
      </Container>
    )
  }

  if (status.kind === 'error') {
    return (
      <Container>
        <Row className='mb-4'>
          <Col>
            <h3 className='mb-0'>Scheduling</h3>
          </Col>
        </Row>
        <Row>
          <Col>
            <SchedulingStatusCard
              icon={<i className='bi bi-exclamation-triangle-fill text-danger' />}
              title="Couldn't load Scheduling"
              subtitle='Something went wrong on our end. Refresh to try again.'
            >
              <Button variant='primary' onClick={refetch}>
                Refresh
              </Button>
            </SchedulingStatusCard>
          </Col>
        </Row>
      </Container>
    )
  }

  if (status.kind !== 'ready') {
    return (
      <Container>
        <Row className='mb-4'>
          <Col>
            <h3 className='mb-0'>Scheduling</h3>
          </Col>
        </Row>
        <Row>
          <Col>
            <SchedulingOnboardingFlow
              status={status}
              onSkipMeetingPlatform={() => setMeetingPlatformSkipped(true)}
              mode={hasCompletedOnboarding ? 'unblock' : 'onboarding'}
            />
          </Col>
        </Row>
      </Container>
    )
  }

  const visibleTabDefinitions = TAB_DEFINITIONS.filter((definition) => definition.key !== 'requests' || showRequestsTab)

  const changeTab = (key: string | null) => {
    if (key) {
      navigate(`/scheduling/${key}`, { replace: true })
    }
  }

  const handleFiltersChange = (nextFilters: SchedulingFilters) => {
    trackingClient.track({
      eventName: TrackEvent.SCHEDULING_FILTERS_CHANGED,
      eventDetails: { activeFilters: Object.keys(nextFilters).filter((key) => nextFilters[key as keyof SchedulingFilters] != null) },
    })
    setFilters(nextFilters)
  }

  return (
    <Container>
      <Row className='mb-4'>
        <Col className='d-flex align-items-center justify-content-between'>
          <h3 className='mb-0'>Scheduling</h3>
          <LinkButton variant='primary' to='/scheduling/schedule'>
            <i className='bi bi-plus-lg me-1' />
            Schedule a meeting
          </LinkButton>
        </Col>
      </Row>
      {showPlatformWarning ? (
        <Row className='mb-4'>
          <Col>
            <MeetingPlatformWarning showSupportLink />
          </Col>
        </Row>
      ) : null}
      <Row className='mb-4'>
        <Col>
          <SchedulingFilterBar eventTypes={eventTypes ?? []} filters={filters} onFiltersChange={handleFiltersChange} />
        </Col>
      </Row>
      <Row>
        <Col>
          <Tabs activeKey={resolvedTab} onSelect={changeTab} className={classNames('mb-4', styles.tabs)} mountOnEnter unmountOnExit>
            {visibleTabDefinitions.map((definition) => (
              <Tab
                key={definition.key}
                eventKey={definition.key}
                tabClassName={definition.key === 'history' ? styles.historyTab : undefined}
                title={<TabTitle definition={definition} count={counts[definition.key]} />}
              >
                {/* Remount the tab content when the filters change so each tab's pagination resets to page 1. */}
                <div className='d-flex flex-column gap-3' key={JSON.stringify(filters)}>
                  <div className='paragraph-small zocks-text-gray-600'>{definition.subtitle}</div>
                  {definition.content(filters)}
                </div>
              </Tab>
            ))}
          </Tabs>
        </Col>
      </Row>
    </Container>
  )
}
